import API_BASE from "../../utils/api";
import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";
import ConfirmDialog from "../../components/ConfirmDialog";

const ACTIONS = [
  { value: "promoted", label: "Promote" },
  { value: "detained", label: "Detain (repeat grade)" },
  { value: "transferred", label: "Transfer (leaving school)" },
  { value: "graduated", label: "Graduate" },
];

const Promotions = () => {
  const [classes, setClasses] = useState([]);
  const [fromClassId, setFromClassId] = useState("");
  const [toAcademicYear, setToAcademicYear] = useState("");
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [roster, setRoster] = useState(null); // { fromClass, suggestedNextClass, roster: [...] }
  const [decisions, setDecisions] = useState({}); // studentId -> { action, toClassId, remarks }
  const [generateFees, setGenerateFees] = useState(false);
  const [feeTemplate, setFeeTemplate] = useState({ feeType: "tuition", amount: "", dueDate: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/classes`).then((res) => setClasses(res.data)).catch(() => {});
    axios.get(`${API_BASE}/settings`).then((res) => {
      const current = res.data?.academicYear || "";
      const match = current.match(/(\d{4})/);
      if (match) {
        const next = parseInt(match[1], 10) + 1;
        setToAcademicYear(current.replace(match[1], String(next)));
      }
    }).catch(() => {});
  }, []);

  const loadRoster = async () => {
    if (!fromClassId) return;
    setLoadingRoster(true);
    setError("");
    setResult(null);
    try {
      const res = await axios.post(`${API_BASE}/promotions/preview`, { fromClassId });
      setRoster(res.data);
      const initial = {};
      res.data.roster.forEach((r) => {
        initial[r.studentId] = {
          action: r.suggestedAction,
          toClassId: r.suggestedClassId || "",
          remarks: "",
        };
      });
      setDecisions(initial);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load roster");
    } finally {
      setLoadingRoster(false);
    }
  };

  const updateDecision = (studentId, field, value) => {
    setDecisions((d) => ({ ...d, [studentId]: { ...d[studentId], [field]: value } }));
  };

  const applySuggestedToAll = () => {
    if (!roster) return;
    const updated = {};
    roster.roster.forEach((r) => {
      updated[r.studentId] = { action: r.suggestedAction, toClassId: r.suggestedClassId || "", remarks: "" };
    });
    setDecisions(updated);
  };

  const handleApply = async () => {
    setSubmitting(true);
    setError("");
    try {
      const decisionList = Object.entries(decisions).map(([studentId, d]) => ({
        studentId,
        action: d.action,
        toClassId: d.action === "promoted" ? d.toClassId : undefined,
        remarks: d.remarks,
      }));
      const body = { fromClassId, toAcademicYear, decisions: decisionList };
      if (generateFees && feeTemplate.feeType && feeTemplate.amount && feeTemplate.dueDate) {
        body.feeTemplate = feeTemplate;
      }
      const res = await axios.post(`${API_BASE}/promotions/apply`, body);
      setResult(res.data);
      setRoster(null);
      setConfirmOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to apply promotions");
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const counts = roster
    ? roster.roster.reduce((acc, r) => {
        const a = decisions[r.studentId]?.action || r.suggestedAction;
        acc[a] = (acc[a] || 0) + 1;
        return acc;
      }, {})
    : {};

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Promotions</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6 space-y-6">
          {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

          {result && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-3">Results</h2>
              <p className="text-sm text-gray-600 mb-3">
                {result.succeeded} of {result.total} processed successfully.
                {result.failed > 0 && ` ${result.failed} failed.`}
              </p>
              <div className="max-h-64 overflow-y-auto border rounded-lg divide-y text-sm">
                {result.results.map((r, i) => (
                  <div key={i} className={`px-3 py-2 flex justify-between ${r.status === "error" ? "text-red-600" : "text-gray-700"}`}>
                    <span>{r.name || r.studentId}</span>
                    <span>{r.status === "error" ? r.error : r.action}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setResult(null)} className="mt-4 text-indigo-600 text-sm hover:underline">Start another batch</button>
            </div>
          )}

          {!result && (
            <>
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">End-of-Year Wizard</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Pick a class, review the whole roster, then promote / detain / transfer / graduate every student in one batch.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Class</label>
                    <select value={fromClassId} onChange={(e) => setFromClassId(e.target.value)} className="w-full border rounded-md px-3 py-2">
                      <option value="">Select class</option>
                      {classes.map((c) => (
                        <option key={c._id} value={c._id}>Class {c.className} - Section {c.section}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Academic Year</label>
                    <input type="text" value={toAcademicYear} onChange={(e) => setToAcademicYear(e.target.value)} placeholder="2026-2027" className="w-full border rounded-md px-3 py-2" />
                  </div>
                  <button
                    onClick={loadRoster}
                    disabled={!fromClassId || loadingRoster}
                    className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loadingRoster ? "Loading…" : "Load Roster"}
                  </button>
                </div>
              </div>

              {roster && (
                <div className="bg-white rounded-lg shadow p-6 space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        {roster.fromClass.className} - {roster.fromClass.section} ({roster.roster.length} students)
                      </h3>
                      {roster.suggestedNextClass ? (
                        <p className="text-sm text-gray-500">Suggested next class: {roster.suggestedNextClass.className} - {roster.suggestedNextClass.section}</p>
                      ) : (
                        <p className="text-sm text-gray-500">No matching next class found — students default to "Graduate" unless changed below.</p>
                      )}
                    </div>
                    <button onClick={applySuggestedToAll} className="text-sm text-indigo-600 hover:underline">Reset to suggested</button>
                  </div>

                  <div className="flex gap-3 flex-wrap text-xs">
                    {Object.entries(counts).map(([action, count]) => (
                      <span key={action} className="px-2 py-1 bg-gray-100 rounded-full text-gray-600 capitalize">{action}: {count}</span>
                    ))}
                  </div>

                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Roll No</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Action</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Target Class</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Remarks</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {roster.roster.map((r) => {
                          const d = decisions[r.studentId] || {};
                          return (
                            <tr key={r.studentId}>
                              <td className="px-4 py-2">{r.rollNumber}</td>
                              <td className="px-4 py-2 font-medium">{r.name}</td>
                              <td className="px-4 py-2">
                                <select
                                  value={d.action || ""}
                                  onChange={(e) => updateDecision(r.studentId, "action", e.target.value)}
                                  className="border rounded-md px-2 py-1"
                                >
                                  {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                              </td>
                              <td className="px-4 py-2">
                                {d.action === "promoted" ? (
                                  <select
                                    value={d.toClassId || ""}
                                    onChange={(e) => updateDecision(r.studentId, "toClassId", e.target.value)}
                                    className="border rounded-md px-2 py-1"
                                  >
                                    <option value="">Select class</option>
                                    {classes.map((c) => (
                                      <option key={c._id} value={c._id}>{c.className} - {c.section}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="text-gray-400 text-xs">—</span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={d.remarks || ""}
                                  onChange={(e) => updateDecision(r.studentId, "remarks", e.target.value)}
                                  className="border rounded-md px-2 py-1 w-full"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="border-t pt-4">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                      <input type="checkbox" checked={generateFees} onChange={(e) => setGenerateFees(e.target.checked)} />
                      Generate a fee record for every continuing student (promoted / detained) — sibling discount tiers apply automatically
                    </label>
                    {generateFees && (
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <select value={feeTemplate.feeType} onChange={(e) => setFeeTemplate({ ...feeTemplate, feeType: e.target.value })} className="border rounded-md px-3 py-2">
                          <option value="tuition">Tuition</option>
                          <option value="transport">Transport</option>
                          <option value="library">Library</option>
                          <option value="sports">Sports</option>
                          <option value="other">Other</option>
                        </select>
                        <input type="number" placeholder="Amount" value={feeTemplate.amount} onChange={(e) => setFeeTemplate({ ...feeTemplate, amount: e.target.value })} className="border rounded-md px-3 py-2" />
                        <input type="date" value={feeTemplate.dueDate} onChange={(e) => setFeeTemplate({ ...feeTemplate, dueDate: e.target.value })} className="border rounded-md px-3 py-2" />
                        <input type="text" placeholder="Description" value={feeTemplate.description} onChange={(e) => setFeeTemplate({ ...feeTemplate, description: e.target.value })} className="border rounded-md px-3 py-2" />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => setConfirmOpen(true)}
                      disabled={submitting}
                      className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Apply to {roster.roster.length} Students
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {confirmOpen && (
        <ConfirmDialog
          title="Apply Promotion Decisions"
          message={`This will update ${roster?.roster.length || 0} student records and cannot be bulk-undone. Continue?`}
          confirmLabel={submitting ? "Applying…" : "Apply"}
          danger
          onConfirm={handleApply}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
};

export default Promotions;
